import { lazy, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useParams } from "react-router";
import { useDistrictDashboard } from "@/api/districts";
import { useResellerDashboard } from "@/api/reseller";
import { useRmDashboard } from "@/api/reseller-manager";
import { DashboardCards } from "@/components/dashboard/dashboard-cards";
import {
	type DateRange,
	DateRangePicker,
	getActivePreset,
	getDefaultDateRange,
	parseLocalDate,
} from "@/components/dashboard/date-range-picker";
import { RecentAlertsSection } from "@/components/dashboard/recent-alerts-section";
import { RmMqttReadingsTable } from "@/components/mqtt/rm-mqtt-readings-table";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";

const MeterChartsSection = lazy(() =>
	import("@/components/dashboard/meter-charts-section").then((m) => ({
		default: m.MeterChartsSection,
	})),
);

import { isValidLang, ROUTE_SLUGS } from "@/config/routes";
import { useAuth } from "@/contexts/auth-context";
import { useDistrict } from "@/contexts/district-context";
import { useReseller } from "@/contexts/reseller-context";
import { getUserRole } from "@/types/auth";

type ViewableRole = "district_manager" | "reseller_manager" | "reseller";

type DistrictSidebarProps = {
	districtId: number | null;
	startDate: string;
	endDate: string;
};

function DistrictAlertsSidebar({
	districtId,
	startDate,
	endDate,
}: DistrictSidebarProps) {
	const { data, isLoading } = useDistrictDashboard({
		district_id: districtId,
		start_date: startDate,
		end_date: endDate,
	});
	return (
		<RecentAlertsSection alarms={data?.recent_alarms} isLoading={isLoading} />
	);
}

type ResellerMqttProps = {
	districtId: number | null;
	startDate: string;
	endDate: string;
};

function ResellerMqttReadings({
	districtId,
	startDate,
	endDate,
}: ResellerMqttProps) {
	const { data } = useResellerDashboard({
		district_id: districtId ?? undefined,
		start_date: startDate,
		end_date: endDate,
	});
	const readings = data?.readings ?? [];
	if (readings.length === 0) return null;
	return <RmMqttReadingsTable readings={readings} />;
}

type RmMqttProps = {
	districtId: number | null;
	resellerId: number | null;
	startDate: string;
	endDate: string;
};

function RmMqttReadings({
	districtId,
	resellerId,
	startDate,
	endDate,
}: RmMqttProps) {
	const { data } = useRmDashboard({
		district_id: districtId ?? undefined,
		reseller_id: resellerId ?? undefined,
		start_date: startDate,
		end_date: endDate,
	});
	const readings = data?.readings ?? [];
	if (readings.length === 0) return null;
	return <RmMqttReadingsTable readings={readings} />;
}

export function DashboardPage() {
	const { user } = useAuth();
	const { districtId, districtName } = useDistrict();
	const { resellerId } = useReseller();
	const { t, i18n } = useTranslation();
	const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange);

	const availableAdminRoles: ViewableRole[] = (() => {
		if (!user) return [];
		const roles: ViewableRole[] = [];
		if (user.reseller_manager_id != null) roles.push("reseller_manager");
		if (user.reseller_id != null) roles.push("reseller");
		if (user.district_manager_id != null) roles.push("district_manager");
		return roles;
	})();
	const [adminViewRole, setAdminViewRole] = useState<ViewableRole>(
		availableAdminRoles.includes("district_manager")
			? "district_manager"
			: (availableAdminRoles[0] ?? "district_manager"),
	);
	const [now, setNow] = useState(() => new Date());
	const { lang } = useParams<{ lang: string }>();
	const currentLang = lang && isValidLang(lang) ? lang : "pl";

	useEffect(() => {
		const id = setInterval(() => setNow(new Date()), 60_000);
		return () => clearInterval(id);
	}, []);

	const role = user ? getUserRole(user) : null;

	if (role === "end_user") {
		return (
			<Navigate
				to={`/${currentLang}/${ROUTE_SLUGS.myApartments[currentLang]}`}
				replace
			/>
		);
	}

	const isAdmin = role === "admin";
	const effectiveRole = isAdmin ? adminViewRole : role;

	const dateRangeLabel = (() => {
		const preset = getActivePreset(dateRange);
		if (preset) return t(`dashboard.dateRange.preset${preset}` as const);
		const fmt = new Intl.DateTimeFormat(i18n.language, {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
		});
		return `${fmt.format(parseLocalDate(dateRange.start_date))} – ${fmt.format(
			parseLocalDate(dateRange.end_date),
		)}`;
	})();

	const asOfLabel = new Intl.DateTimeFormat(i18n.language, {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	}).format(now);

	const contextName =
		districtName ?? (t("districtSelector.allDistricts") as string);

	const leftContent = (() => {
		if (effectiveRole === "district_manager") {
			return (
				<Suspense fallback={<Skeleton className="h-80 w-full" />}>
					<MeterChartsSection
						districtId={districtId}
						startDate={dateRange.start_date}
						endDate={dateRange.end_date}
					/>
				</Suspense>
			);
		}
		if (effectiveRole === "reseller") {
			return (
				<ResellerMqttReadings
					districtId={districtId}
					startDate={dateRange.start_date}
					endDate={dateRange.end_date}
				/>
			);
		}
		if (effectiveRole === "reseller_manager") {
			return (
				<RmMqttReadings
					districtId={districtId}
					resellerId={resellerId}
					startDate={dateRange.start_date}
					endDate={dateRange.end_date}
				/>
			);
		}
		return null;
	})();

	const rightSidebar =
		effectiveRole === "district_manager" ? (
			<DistrictAlertsSidebar
				districtId={districtId}
				startDate={dateRange.start_date}
				endDate={dateRange.end_date}
			/>
		) : null;

	return (
		<div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
			<div className="@container/main flex flex-1 flex-col gap-2">
				<div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
					<div className="flex flex-wrap items-center gap-3 px-4 lg:px-6">
						<div className="flex flex-col">
							<p className="text-muted-foreground text-sm">
								{contextName} · {dateRangeLabel} · {t("dashboard.asOf")}{" "}
								{asOfLabel}
							</p>
						</div>
						<div className="ml-auto flex flex-wrap items-center gap-3">
							{isAdmin && availableAdminRoles.length > 1 && (
								<ToggleGroup
									type="single"
									value={adminViewRole}
									onValueChange={(v) => {
										if (v) setAdminViewRole(v as ViewableRole);
									}}
									variant="outline"
								>
									{availableAdminRoles.includes("reseller_manager") && (
										<ToggleGroupItem value="reseller_manager">
											{t("dashboard.viewAs.resellerManager")}
										</ToggleGroupItem>
									)}
									{availableAdminRoles.includes("reseller") && (
										<ToggleGroupItem value="reseller">
											{t("dashboard.viewAs.reseller")}
										</ToggleGroupItem>
									)}
									{availableAdminRoles.includes("district_manager") && (
										<ToggleGroupItem value="district_manager">
											{t("dashboard.viewAs.districtManager")}
										</ToggleGroupItem>
									)}
								</ToggleGroup>
							)}
							<DateRangePicker value={dateRange} onChange={setDateRange} />
						</div>
					</div>
					<DashboardCards
						districtId={districtId}
						startDate={dateRange.start_date}
						endDate={dateRange.end_date}
						viewRole={isAdmin ? adminViewRole : undefined}
						resellerId={resellerId}
					/>
					<div
						className={cn(
							"grid grid-cols-1 gap-4 px-4 lg:px-6",
							rightSidebar && "lg:grid-cols-3",
						)}
					>
						<div className={cn(rightSidebar && "lg:col-span-2")}>
							{leftContent}
						</div>
						{rightSidebar && (
							<div className="lg:relative lg:col-span-1">
								<div className="lg:absolute lg:inset-0">{rightSidebar}</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
